// "Arkadaş ekle" — privacy-respecting discovery. Three ways to find people,
// none of which exposes the whole player directory:
//   1. Suggestions — people you've played with + friends-of-friends (only those
//      whose profile allows it). Each can be dismissed so it won't reappear.
//   2. Invite code — add someone directly by the code they shared with you.
//   3. Name search — only finds people who made their profile public.
// Adding is a request → accept flow, so a freshly added person shows as pending.

import $ from 'jquery';
import type { PersonResult, PersonSuggestion } from '@shared/dto';
import type { Screen } from './types';
import { api } from '../api';
import { getCurrentUser, refreshUnread } from '../session';
import { avatarHtml, esc } from '../ui/dom';
import { SKILL_TR, skillClass } from '../ui/format';
import { emptyHtml, loadingHtml } from '../ui/states';
import { toast } from '../ui/toast';

function actionHtml(relation: PersonResult['relation'], user: { id: string }, requestId?: string): string {
  switch (relation) {
    case 'friend':
      return `<span class="badge badge--friend">✓ Arkadaş</span>`;
    case 'outgoing':
      return `<button class="btn btn--ghost btn--sm" disabled>⏳ Gönderildi</button>`;
    case 'incoming':
      return `<button class="btn btn--primary btn--sm" data-accept="${esc(requestId)}">Kabul et</button>`;
    default:
      return `<button class="btn btn--primary btn--sm" data-add="${esc(user.id)}">+ Ekle</button>`;
  }
}

function suggestionRowHtml(s: PersonSuggestion): string {
  const u = s.user;
  return `<div class="roster__row">
    ${avatarHtml(u.name, u.avatarUrl, 36)}
    <span class="roster__name">${esc(u.name)}
      <span class="reason-chip">${s.source === 'co-player' ? '🏀 ' : '👥 '}${esc(s.reason)}</span>
    </span>
    <button class="btn btn--primary btn--sm" data-add="${esc(u.id)}">+ Ekle</button>
    <button class="btn btn--ghost btn--sm icon-btn" data-dismiss="${esc(u.id)}" aria-label="Öneriyi kaldır" title="Kaldır">✕</button>
  </div>`;
}

function searchRowHtml(p: PersonResult): string {
  const u = p.user;
  return `<div class="roster__row">
    ${avatarHtml(u.name, u.avatarUrl, 36)}
    <span class="roster__name">${esc(u.name)}</span>
    <span class="roster__area">${esc(u.homeArea)}</span>
    <span class="badge ${skillClass(u.skill)}">${SKILL_TR[u.skill]}</span>
    ${actionHtml(p.relation, u, p.requestId)}
  </div>`;
}

export const AddFriendsScreen: Screen = async (body) => {
  body.html(loadingHtml());
  const me = getCurrentUser();

  let suggestions: PersonSuggestion[] = await api.call('friends.suggestions', undefined);

  const myCode = me?.inviteCode ?? '';

  body.html(`
    <div class="people">
      <div class="section-title">Önerilenler</div>
      <p class="muted">Birlikte oynadığın kişiler ve ortak arkadaşların.</p>
      <div class="roster" id="suggestion-list"></div>

      <div class="section-title people__divider">Davet koduyla ekle</div>
      ${
        myCode
          ? `<p class="muted">Senin kodun: <span class="invite-code">${esc(myCode)}</span> — paylaşarak sana istek göndermelerini sağla.</p>`
          : ''
      }
      <div class="field--row">
        <input class="input" id="code-input" placeholder="Davet kodunu yapıştır" autocomplete="off" />
        <button class="btn btn--primary btn--sm" id="code-add">İstek gönder</button>
      </div>

      <div class="section-title people__divider">İsimle ara</div>
      <p class="muted">Yalnızca profilini herkese açık yapan kişiler bulunur.</p>
      <input class="input people__search" type="search" placeholder="İsimle ara…" autocomplete="off" />
      <div class="roster" id="search-list"></div>
    </div>
  `);

  const suggestionList = $('#suggestion-list');
  const searchList = $('#search-list');

  const renderSuggestions = (): void => {
    suggestionList.html(
      suggestions.length
        ? suggestions.map(suggestionRowHtml).join('')
        : emptyHtml('✨', 'Şimdilik öneri yok. Oynadıkça buraya kişiler gelecek.'),
    );
  };
  renderSuggestions();

  const reloadSuggestions = async (): Promise<void> => {
    suggestions = await api.call('friends.suggestions', undefined);
    renderSuggestions();
    void refreshUnread();
  };

  // --- Name search (public profiles only) ---------------------------------
  const runSearch = async (query: string): Promise<void> => {
    const q = query.trim();
    if (!q) {
      searchList.empty();
      return;
    }
    const results = await api.call('friends.search', { query: q });
    searchList.html(
      results.length ? results.map(searchRowHtml).join('') : emptyHtml('🔍', 'Herkese açık eşleşme yok.'),
    );
  };

  $('.people__search').on('input', function () {
    void runSearch(($(this).val() as string) ?? '');
  });

  // --- Add by invite code --------------------------------------------------
  $('#code-add').on('click', async () => {
    const code = ($('#code-input').val() as string)?.trim() ?? '';
    if (!code) {
      toast('Bir davet kodu gir', 'error');
      return;
    }
    try {
      const res = await api.call('friends.addByCode', { code });
      if (res.relation === 'friend') toast(`${res.user.name} ile artık arkadaşsınız 🤝`, 'success');
      else toast(`${res.user.name} kişisine istek gönderildi ⏳`, 'success');
      $('#code-input').val('');
      await reloadSuggestions();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  // --- Shared actions (delegated; lists re-render) -------------------------
  body.on('click', '[data-add]', async function () {
    const toId = $(this).data('add') as string;
    try {
      await api.call('friends.request', { toId });
      toast('Arkadaşlık isteği gönderildi ⏳', 'success');
      await reloadSuggestions();
      await runSearch(($('.people__search').val() as string) ?? '');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-dismiss]', async function () {
    const userId = $(this).data('dismiss') as string;
    try {
      await api.call('friends.dismissSuggestion', { userId });
      suggestions = suggestions.filter((s) => s.user.id !== userId);
      renderSuggestions();
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });

  body.on('click', '[data-accept]', async function () {
    const requestId = $(this).data('accept') as string;
    try {
      await api.call('friends.respond', { requestId, accept: true });
      toast('Artık arkadaşsınız 🤝', 'success');
      await reloadSuggestions();
      await runSearch(($('.people__search').val() as string) ?? '');
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  });
};
